import { Building2, ChevronRight, CornerDownRight } from "lucide-react";
import { useGetList, useRecordContext } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";

import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Deal } from "../types";

/**
 * Company hierarchy - holding-company chains with drill-down and roll-ups.
 *
 * Walks parent_company_id upward for the ownership chain and downward for
 * subsidiaries (any depth). The roll-up card aggregates contacts, open
 * pipeline, and closed won across the company itself plus every descendant,
 * so a holding company reads as the whole group at a glance.
 *
 * Computed client-side over the dataProvider at pilot scale; moves to a
 * recursive CTE with Phase 7 reporting.
 */

const MAX_CHAIN = 12; // defensive bound when walking up

export const CompanyHierarchy = () => {
  const record = useRecordContext<Company>();
  const { currency } = useConfigurationContext();
  const { data: companies = [] } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const tree = useMemo(() => {
    if (!record) return null;
    const byId = new Map(companies.map((c) => [c.id, c]));

    // Ancestor chain, closest parent last
    const ancestors: Company[] = [];
    let cursor = byId.get(record.id) ?? record;
    for (let i = 0; i < MAX_CHAIN; i++) {
      const parent =
        cursor.parent_company_id != null
          ? byId.get(cursor.parent_company_id)
          : undefined;
      if (!parent) break;
      ancestors.unshift(parent);
      cursor = parent;
    }

    // Direct children + all descendants (BFS; children ids strictly differ)
    const childrenOf = (id: Company["id"]) =>
      companies.filter((c) => c.parent_company_id === id);
    const children = childrenOf(record.id);
    const descendants: Company[] = [];
    const queue = [...children];
    const seen = new Set<Company["id"]>([record.id]);
    while (queue.length) {
      const next = queue.shift()!;
      if (seen.has(next.id)) continue;
      seen.add(next.id);
      descendants.push(next);
      queue.push(...childrenOf(next.id));
    }

    return { ancestors, children, descendants, childrenOf };
  }, [record, companies]);

  if (!record || !tree) return null;
  const { ancestors, children, descendants } = tree;
  if (ancestors.length === 0 && children.length === 0) return null;

  const groupIds = new Set([record.id, ...descendants.map((c) => c.id)]);
  const groupDeals = deals.filter(
    (d) => d.company_id != null && groupIds.has(d.company_id) && !d.archived_at,
  );
  const isClosed = (d: Deal) => d.stage === "won" || d.stage === "lost";
  const openPipeline = groupDeals
    .filter((d) => !isClosed(d))
    .reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const closedWon = groupDeals
    .filter((d) => d.stage === "won")
    .reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const groupContacts = [record, ...descendants].reduce(
    (s, c) => s + (c.nb_contacts ?? 0),
    0,
  );

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  return (
    <AsideSection title="Company hierarchy">
      {/* Ownership chain */}
      {ancestors.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm">
          {ancestors.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <Link
                to={`/companies/${c.id}/show`}
                className="underline hover:no-underline inline-flex items-center gap-1"
              >
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                {c.name}
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            </span>
          ))}
          <span className="font-medium">{record.name}</span>
        </div>
      )}

      {/* Subsidiaries, two levels visible, deeper via drill-down */}
      {children.length > 0 && (
        <div className="flex flex-col gap-0.5 text-sm">
          <p className="text-xs text-muted-foreground">
            {children.length} direct subsidiar{children.length === 1 ? "y" : "ies"}
            {descendants.length > children.length
              ? ` · ${descendants.length} total in group`
              : ""}
          </p>
          {children.map((c) => {
            const grandchildren = tree.childrenOf(c.id);
            return (
              <div key={c.id} className="flex flex-col">
                <Link
                  to={`/companies/${c.id}/show`}
                  className="underline hover:no-underline inline-flex items-center gap-1"
                >
                  <CornerDownRight className="w-3.5 h-3.5 text-muted-foreground" />
                  {c.name}
                </Link>
                {grandchildren.map((gc) => (
                  <Link
                    key={gc.id}
                    to={`/companies/${gc.id}/show`}
                    className="underline hover:no-underline inline-flex items-center gap-1 pl-5 text-muted-foreground"
                  >
                    <CornerDownRight className="w-3 h-3" />
                    {gc.name}
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Group roll-up */}
      {descendants.length > 0 && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 mt-1">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Group roll-up · {descendants.length + 1} companies
          </p>
          <div className="grid grid-cols-3 gap-2 text-sm tabular-nums">
            <div>
              <p className="font-semibold">{groupContacts}</p>
              <p className="text-[11px] text-muted-foreground">contacts</p>
            </div>
            <div>
              <p className="font-semibold">{fmt(openPipeline)}</p>
              <p className="text-[11px] text-muted-foreground">open pipeline</p>
            </div>
            <div>
              <p className="font-semibold">{fmt(closedWon)}</p>
              <p className="text-[11px] text-muted-foreground">closed won</p>
            </div>
          </div>
        </div>
      )}
    </AsideSection>
  );
};
